import { useEffect } from "react";
import { TrendingUp, Target, ArrowUpDown, Zap, Feather, ArrowRight } from "lucide-react";
import { Reveal, ASSETS } from "@/lib/motion";
import Nav from "@/components/bwg/Nav";
import Footer from "@/components/bwg/Footer";
import WhatsAppFab from "@/components/bwg/WhatsAppFab";
import InteriorHero from "@/components/bwg/InteriorHero";
import Seo from "@/components/bwg/Seo";
import { orgSchema, breadcrumbSchema, CONTACT } from "@/lib/schema";

const CULTURE = [
  { icon: TrendingUp, title: "Pay grows with performance", body: "Compensation moves with the value you create, not with an annual increment cycle." },
  { icon: Target, title: "Real deals, no busy-work", body: "Your work connects directly to live deals and real decisions. Nothing you do is theatre." },
  { icon: ArrowUpDown, title: "Roles expand", body: "We promote from within by default. Take responsibility and the role grows around you." },
  { icon: Zap, title: "Learning velocity", body: "Proximity to founders and real capital decisions compounds what you learn, fast." },
  { icon: Feather, title: "Lean by design", body: "No bureaucracy and minimal process overhead: a lean operating style that respects your time." },
];

export default function Careers() {
  useEffect(() => { window.scrollTo(0, 0); }, []);
  return (
    <main className="bg-brand-paper overflow-x-hidden">
      <Seo
        title="Careers at Bhaarat Wealth Group | Work on Real Land Deals"
        description="Changing India's real estate landscape, forever. Join Bhaarat Wealth Group for pay that grows with performance, real deal exposure and learning velocity from proximity to founders. Open applications."
        path="/careers"
        schema={[orgSchema(), breadcrumbSchema([{ name: "Home", path: "/" }, { name: "Careers", path: "/careers" }])]}
      />
      <Nav overHero />
      <InteriorHero img={ASSETS.hero} eyebrow="Careers"
        title={<>Changing India's real estate landscape. <span className="text-brand-orange">Forever.</span></>}
        sub="A lean team working on real land deals across India's fastest-moving corridors. No busy-work and no bureaucracy, just proximity to real decisions." />

      <section className="bg-brand-offwhite py-24 lg:py-32">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <Reveal>
            <span className="text-brand-orange text-sm font-semibold tracking-[0.2em] uppercase">Why BWG</span>
            <h2 className="mt-4 font-display font-semibold text-4xl sm:text-5xl tracking-tighter text-brand-ink leading-[1.05] max-w-2xl">How it actually works here.</h2>
          </Reveal>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6 mt-14">
            {CULTURE.map((c, i) => (
              <Reveal key={c.title} delay={i * 0.1}>
                <div className="h-full bg-brand-card border border-brand-ink/10 rounded-sm p-8 hover:border-brand-orange transition-colors duration-500">
                  <c.icon className="w-8 h-8 text-brand-orange mb-5" strokeWidth={1.5} />
                  <h3 className="font-display font-semibold text-xl tracking-tight text-brand-ink">{c.title}</h3>
                  <p className="mt-3 text-brand-grey leading-relaxed text-[15px]">{c.body}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <section className="bg-brand-dark py-28 lg:py-36 relative overflow-hidden">
        <div className="grain" />
        <div className="max-w-4xl mx-auto px-6 lg:px-8 relative z-10 text-center">
          <Reveal>
            <h2 className="font-display font-semibold text-4xl sm:text-5xl lg:text-6xl tracking-tighter text-brand-offwhite leading-[1.02]">We read everything.</h2>
            <p className="mt-6 text-lg text-brand-offwhite/65 leading-relaxed max-w-2xl mx-auto">
              We don't run formal job listings. Drop us a note and tell us who you are and what you're good at. Short is fine.
            </p>
            <a href={`mailto:${CONTACT.careersEmail}?subject=Working with BWG`} data-testid="careers-cta"
              className="mt-10 inline-flex items-center gap-2 rounded-full bg-brand-orange px-8 py-4 font-semibold text-white hover:bg-brand-offwhite hover:text-brand-ink transition-colors">
              {CONTACT.careersEmail} <ArrowRight className="w-5 h-5" />
            </a>
          </Reveal>
        </div>
      </section>

      <Footer />
      <WhatsAppFab />
    </main>
  );
}
